// Imports
import { faText, faLink } from '@fortawesome/pro-solid-svg-icons';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '../../../../shareables/foundation/admin-ui/components/content/box';
import Heading from '../../../../shareables/foundation/admin-ui/components/content/heading';
import Input from '../../../../shareables/foundation/front-end/components/forms/fields/input';
import Radio from '../../../../shareables/foundation/front-end/components/forms/fields/radio';
import { useAppDispatch } from '../../../../shareables/foundation/front-end/redux/hooks';
import APIForm, { APIFormOptions } from '../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../shareables/integrations/api/redux/actions';
import { PostWebsiteParameters } from '../../../../shareables/types/api/main/reseller/website/post';
import formatCurrency from '../../../../shareables/utils/project-specific/formatting/format-currency';
import subscriptionAmountMapping from '../../../../shareables/utils/billing/subscription-amount-mapping';
import {
	GetRecommendationParameters,
	GetRecommendationResponse,
} from '../../../../shareables/types/api/main/domain/recommendation/get';
import cleanseDomain from '../../../../shareables/utils/project-specific/formatting/cleanse-domain';
import ResellerContext from '../../../resources/reseller-provider/context';
import { getFingerprint } from '@thumbmarkjs/thumbmarkjs';
import * as Sentry from '@sentry/browser';
import planMapping from '../../../../shareables/utils/billing/plan-mapping';
import planNameMapping from '../../../../shareables/utils/billing/plan-name-mapping';
import availableSubscriptionSizes from '../../../../shareables/utils/billing/available-subscription-sizes';
import extendedPricingDetails from '../../../../shareables/utils/billing/extended-pricing-details';
import Select from '../../../../shareables/foundation/front-end/components/forms/fields/select';
import styled from 'styled-components/macro';
import determineMonthlySubscriptionAmount from '../../../../shareables/utils/billing/determine-monthly-subscription-amount';
import PlanSelector from '../../../../shareables/front-end/subscription-plans/plan-selector';


// Styled components
const ExtendedSize = styled.div`
	div {
		display: inline-block;
		margin-bottom: 0 !important;
	}
	
	select {
		height: 1.5625rem;
	}
	
	select + span {
		top: -0.4063rem;
	}
`;


// Define the accepted props
interface Props {
	storybookCommunityName?: string;
	storybookSubscriptionSize?: `${number}`;
	storybookLoadingRecommendation?: true;
	storybookDisplayTaxVerbiage?: true;
	storybookSelectedPlan?: number;
}


// Function component
const CreateWebsite: React.FC<Props> = ({
	storybookCommunityName,
	storybookSubscriptionSize,
	storybookLoadingRecommendation,
	storybookDisplayTaxVerbiage,
	storybookSelectedPlan,
}) => {
	// Use context
	const reseller = useContext(ResellerContext);
	
	
	// Use state
	const [loadingRecommendation, setLoadingRecommendation] = useState(storybookLoadingRecommendation ?? false);
	const [fingerprint, setFingerprint] = useState<string | null>(null);
	const [selectedPlan, setSelectedPlan] = useState<number>(storybookSelectedPlan ?? planMapping.engage);
	const [selectedSize, setSelectedSize] = useState<number>();
	const [dropdownSize, setDropdownSize] = useState<number>();
	const [selectedPlanChanged, setSelectedPlanChanged] = useState(Boolean(storybookSelectedPlan));
	const [planErrorMessage, setPlanErrorMessage] = useState('');
	
	
	// Use ref
	const domainGetter = useRef<() => string>();
	const domainSetter = useRef<(value: string) => void>();
	const communityNameGetter = useRef<() => string>();
	const selectListSizeGetter = useRef<() => string | number>();
	const radioSizeSetter = useRef<(value: string) => void>();
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use React Router functionality
	const navigate = useNavigate();
	
	
	// Create fingerprint on mount
	useEffect(() => {
		void (async () => {
			try {
				setFingerprint(await getFingerprint());
			} catch (error: unknown) {
				// Report to Sentry
				Sentry.captureException(error);
			}
		})();
	}, []);
	
	
	// Update dropdown size when Engage plan is selected
	//  -> This prevents an error when selecting the dropdown option without making any changes and then submitting the form
	useEffect(() => {
		if (
			selectedPlan !== undefined &&
			Object.prototype.hasOwnProperty.call(extendedPricingDetails, selectedPlan) &&
			selectListSizeGetter.current
		) {
			setDropdownSize(Number(selectListSizeGetter.current()));
		}
	}, [selectedPlan]);
	
	
	// Build form options
	const formOptions: APIFormOptions<PostWebsiteParameters, undefined, undefined> = {
		action: 'POST',
		uri: `/reseller/${reseller?.id ?? ''}/website`,
		submitButton: {
			text: 'Create',
		},
		extraParameters: {
			size: selectedSize,
			...(fingerprint ? { adClickFingerprint: fingerprint } : {}),
		},
		errorHandlers: [
			{
				match: {
					code: 'PARAMETER_ERROR',
					parameter: 'plan',
				},
				handler: (json) => {
					if (json.message.toLowerCase().includes('this is required')) {
						setPlanErrorMessage('Please select a plan above.');
						return;
					}
					
					setPlanErrorMessage(json.message);
				},
			},
		],
	};
	
	
	// Build size choices
	const sizeChoices = useMemo(() => {
		// Type guard
		const isValidPlan = (plan: number): plan is (typeof planMapping)[keyof typeof planMapping] => {
			return Object.keys(planNameMapping).map(Number).includes(plan);
		};
		
		
		// Return early
		if (selectedPlan === undefined || !isValidPlan(selectedPlan)) {
			return [];
		}
		
		
		// Decide whether tax verbiage should be displayed
		const displaySalesTaxVerbiage = reseller?.regionRequiresSalesTax || storybookDisplayTaxVerbiage;
		
		
		// Create base size choices
		const baseSizeChoices = Object.keys(subscriptionAmountMapping[selectedPlan]).map((sizeChoice) => ({
			label: (
				<React.Fragment>
					{Number(sizeChoice).toLocaleString()} accounts
					{selectedPlanChanged && (
						<small className='is-gray'>
							{formatCurrency(
								determineMonthlySubscriptionAmount({
									plan: selectedPlan,
									size: Number(sizeChoice),
									yearly: false,
								}).value * 0.75
							)}
							/mo{displaySalesTaxVerbiage ? ' + tax' : ''}
						</small>
					)}
				</React.Fragment>
			),
			value: sizeChoice,
		}));
		
		
		// Return Inform plan size choices
		if (selectedPlan === planMapping.inform) {
			return [
				...baseSizeChoices,
				{
					label: (
						<span className='is-gray'>
							<em>
								{Number(baseSizeChoices[baseSizeChoices.length - 1].value) + 1}+ accounts requires the Engage plan
							</em>
						</span>
					),
					value: 'disabled',
					disabled: true,
				},
			];
		}
		
		
		// Create extended size choices
		const allSizes = availableSubscriptionSizes(selectedPlan);
		
		const baseSizeSet = new Set(baseSizeChoices.map((choice) => Number(choice.value)));
		
		const extendedPlanSizes = allSizes.filter((size) => !baseSizeSet.has(size));
		
		const extendedSizeChoices = (
			<ExtendedSize>
				<Select
					name='extendedSizeChoice'
					exclude
					bindGetter={(getter) => (selectListSizeGetter.current = getter)}
					onChange={(newValue) => {
						setSelectedSize(Number(newValue));
						setDropdownSize(Number(newValue));
					}}
					onClick={() => radioSizeSetter.current?.('selectList')}
				>
					{extendedPlanSizes.map((size) => {
						return (
							<option key={size} value={size}>
								{size.toLocaleString()}
							</option>
						);
					})}
				</Select>{' '}
				accounts
				{selectedPlanChanged && (
					<small className='is-gray'>
						{formatCurrency(
							determineMonthlySubscriptionAmount({
								plan: selectedPlan,
								size: dropdownSize ?? extendedPlanSizes[0],
								yearly: false,
							}).value * 0.75
						)}
						/mo{displaySalesTaxVerbiage ? ' + tax' : ''}
					</small>
				)}
			</ExtendedSize>
		);
		
		
		// Return Engage plan size choices
		return [
			...baseSizeChoices,
			{
				label: extendedSizeChoices,
				value: 'selectList',
			},
		];
	}, [selectedPlan, dropdownSize, reseller?.regionRequiresSalesTax, storybookDisplayTaxVerbiage, selectedPlanChanged]);
	
	
	// Handle community name blur
	const handleCommunityNameBlur = useCallback(() => {
		const domain = domainGetter.current?.();
		const communityName = communityNameGetter.current?.();
		
		if (!domain && communityName) {
			setLoadingRecommendation(true);
			
			void dispatch(
				apiActions.call<GetRecommendationParameters, undefined, GetRecommendationResponse>({
					action: 'GET',
					uri: `/domain/recommendation`,
					parameters: {
						type: 'TEMPORARY_ADDRESS',
						communityName: communityName,
					},
					completion: (json) => {
						if (json.response.recommendation.length) {
							domainSetter.current?.(cleanseDomain(json.response.recommendation));
						}
						
						setLoadingRecommendation(false);
					},
				})
			);
		}
	}, [dispatch]);
	
	
	// Return JSX
	return (
		<React.Fragment>
			<Heading size={1}>Create website</Heading>
			
			<APIForm
				options={formOptions}
				onCompletion={() => {
					window.location.href = `https://start.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/`;
				}}
				onCancel={() => navigate(-1)}
			>
				<Box>
					<Input
						type='text'
						name='communityName'
						label='Community name'
						icon={faText}
						maxLength={75}
						placeholder='Sunnyville HOA'
						onBlur={handleCommunityNameBlur}
						initialValue={storybookCommunityName}
						bindGetter={(getter) => (communityNameGetter.current = getter)}
						autoFocus
						required
					/>
					
					<Input
						type='text'
						name='websiteAddress'
						label='Temporary website address'
						note='Letters, numbers, and dashes'
						placeholder={loadingRecommendation ? 'Loading recommendation…' : 'communityname'}
						disabled={loadingRecommendation}
						suffix={
							(window as typeof window & { STORYBOOK?: true }).STORYBOOK
								? '.communitysite.com'
								: `.${process.env.REACT_APP__CUSTOMER_SITES_HOSTNAME}`
						}
						maxLength={50}
						icon={faLink}
						bindGetter={(getter) => (domainGetter.current = getter)}
						bindSetter={(setter) => (domainSetter.current = setter)}
						required
					/>
				</Box>
				
				<Box>
					<PlanSelector
						fieldLabel='Subscription plan'
						handleSelectedPlanChange={(newValue) => {
							setSelectedPlan(Number(newValue));
							setSelectedPlanChanged(true);
							setPlanErrorMessage('');
						}}
						selectedPlan={selectedPlan}
						selectedPlanChanged={selectedPlanChanged}
						errorMessage={planErrorMessage}
					/>
					
					<Radio
						name='size'
						label='Subscription size'
						note={
							<React.Fragment>
								{/* eslint-disable-next-line react/jsx-no-target-blank */}
								<a
									href='https://help.hoa-express.com/en/articles/6698499-selecting-a-subscription-size'
									target='_blank'
								>
									Learn more
								</a>{' '}
								about selecting the right size
							</React.Fragment>
						}
						initialValue={storybookSubscriptionSize}
						choices={sizeChoices}
						required
						exclude
						onChange={(newValue) => {
							if (newValue === 'selectList') {
								setSelectedSize(Number(selectListSizeGetter.current?.()));
								return;
							}
							
							if (!isNaN(Number(newValue))) {
								setSelectedSize(Number(newValue));
							}
						}}
						bindSetter={(setter) => (radioSizeSetter.current = setter)}
					/>
				</Box>
				
				<Box>
					You will be immediately charged for your newly-created website, based on the subscription size chosen above.
					Resellers with at least one resold website are required to spend a minimum of $100/mo.
				</Box>
			</APIForm>
		</React.Fragment>
	);
};

export default CreateWebsite;
